import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import TimesheetSettings from './TimesheetSettings';
import {
  LogIn, LogOut, AlertTriangle, Clock, CheckCircle, XCircle, FileSpreadsheet,
  ChevronDown, ChevronRight, Search, X, Filter, Users, Briefcase, Calendar,
  Edit2, Trash2, History, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight as ChevronRightIcon, QrCode
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';

const TimesheetManagement = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('timesheets');
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    total_hours: '0h',
    approved_hours: '0h',
    pending: 0,
    approved: 0,
    rejected: 0,
    admin_types: [],
    categories: [],
    work_types: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    status: [],
    users: [],
    projects: [],
    categories: [],
    types: [],
    date_from: '',
    date_to: ''
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [typeSearch, setTypeSearch] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    break_duration: 0,
    type: '',
    projekt: '',
    admin_type: '',
    work_subtype: '',
    note: ''
  });
  const [adminTypes, setAdminTypes] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyTimesheet, setHistoryTimesheet] = useState(null);
  const [showSingleRejectModal, setShowSingleRejectModal] = useState(false);
  const [rejectingTimesheetId, setRejectingTimesheetId] = useState(null);
  const [singleRejectionReason, setSingleRejectionReason] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedSummaryCard, setSelectedSummaryCard] = useState(null); // 'approved', 'pending', or null
  const [workTypes, setWorkTypes] = useState([]);
  const [workCategories, setWorkCategories] = useState([]);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [discrepancyData, setDiscrepancyData] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteTimesheetId, setDeleteTimesheetId] = useState(null);
  const [expandedProjectCard, setExpandedProjectCard] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    fetchTimesheets();
    fetchStats();
  }, [filters, currentPage]);

  useEffect(() => {
    fetchUsersAndProjects();
    fetchWorkTypes();
    fetchAdminTypes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown-container')) {
        setShowStatusDropdown(false);
        setShowTypeDropdown(false);
        setShowUserDropdown(false);
        setShowProjectDropdown(false);
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const fetchUsersAndProjects = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [usersResponse, projectsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/users/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/projects/`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAvailableUsers(usersResponse.data || []);
      setAvailableProjects(projectsResponse.data || []);
    } catch (error) {
      console.error('Error fetching users/projects:', error);
    }
  };

  const fetchWorkTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [categoriesResponse, typesResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/work-categories/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/work-types/`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setWorkCategories(categoriesResponse.data || []);
      setWorkTypes(typesResponse.data || []);
    } catch (error) {
      console.error('Error fetching work types:', error);
    }
  };

  const fetchAdminTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin-types/`, { headers: { Authorization: `Bearer ${token}` } });
      setAdminTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching admin types:', error);
    }
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => { if (v) params.append(key, v); });
        } else if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/?${params}`, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.results) {
        setTimesheets(response.data.results || []);
        setTotalPages(response.data.pagination?.num_pages || 1);
        setTotalCount(response.data.pagination?.count || 0);
      } else {
        setTimesheets(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => { if (v) params.append(key, v); });
        } else if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/stats/?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(timesheets.map(ts => ts.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleApprove = async () => {
    if (selectedIds.length === 0) {
      showToastMessage('Please select timesheets to approve');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/approve/`, { timesheet_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage(`${selectedIds.length} timesheet(s) approved`);
      setSelectedIds([]);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error approving timesheets');
    }
  };

  const handleReject = () => {
    if (selectedIds.length === 0) {
      showToastMessage('Please select timesheets to reject');
      return;
    }
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/reject/`, { timesheet_ids: selectedIds, rejection_reason: rejectionReason }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage(`${selectedIds.length} timesheet(s) rejected`);
      setSelectedIds([]);
      setShowRejectModal(false);
      setRejectionReason('');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error rejecting timesheets');
    }
  };

  const handleDelete = () => {
    if (selectedIds.length === 0) {
      showToastMessage('Please select timesheets to delete');
      return;
    }
    setDeleteType('bulk');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const timesheetIds = deleteType === 'single' ? [deleteTimesheetId] : selectedIds;
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/delete/`, { timesheet_ids: timesheetIds }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage(`${timesheetIds.length} timesheet(s) deleted`);
      setSelectedIds([]);
      setShowDeleteModal(false);
      setDeleteType(null);
      setDeleteTimesheetId(null);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error deleting timesheets');
      setShowDeleteModal(false);
    }
  };

  const handleEditClick = (timesheet) => {
    setEditingTimesheet(timesheet);
    setEditFormData({
      date: timesheet.date,
      start_time: timesheet.start_time,
      end_time: timesheet.end_time,
      break_duration: timesheet.break_duration || 0,
      type: timesheet.type,
      projekt: timesheet.projekt?.id || timesheet.project?.id || '',
      admin_type: timesheet.admin_type?.id || '',
      work_subtype: timesheet.work_subtype?.id || '',
      note: timesheet.note || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/${editingTimesheet.id}/edit/`, editFormData, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage('Timesheet updated successfully');
      setShowEditModal(false);
      setEditingTimesheet(null);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error updating timesheet');
    }
  };

  const handleHistoryClick = (timesheet) => {
    setHistoryTimesheet(timesheet);
    setShowHistoryModal(true);
  };

  const handleSingleDelete = (timesheetId) => {
    setDeleteTimesheetId(timesheetId);
    setDeleteType('single');
    setShowDeleteModal(true);
  };

  const handleSingleRejectClick = (timesheetId) => {
    setRejectingTimesheetId(timesheetId);
    setShowSingleRejectModal(true);
  };

  const confirmSingleReject = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/reject/`, { timesheet_ids: [rejectingTimesheetId], rejection_reason: singleRejectionReason }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage('Timesheet rejected');
      setShowSingleRejectModal(false);
      setRejectingTimesheetId(null);
      setSingleRejectionReason('');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error rejecting timesheet');
    }
  };

  const handleSingleApprove = async (timesheetId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/approve/`, { timesheet_ids: [timesheetId] }, { headers: { Authorization: `Bearer ${token}` } });
      showToastMessage('Timesheet approved');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      showToastMessage('Error approving timesheet');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-600',
      approved: 'bg-emerald-50 text-emerald-600',
      rejected: 'bg-red-50 text-red-600',
      'partially-approved': 'bg-blue-50 text-blue-600',
      mixed: 'bg-violet-50 text-violet-600'
    };
    const dots = {
      pending: 'bg-amber-500',
      approved: 'bg-emerald-500',
      rejected: 'bg-red-500',
      'partially-approved': 'bg-blue-500',
      mixed: 'bg-violet-500'
    };
    const labels = {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      'partially-approved': 'Partial',
      mixed: 'Mixed'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md ${styles[status] || styles.pending}`}>
        <span className={`w-1 h-1 rounded-full ${dots[status] || dots.pending}`} />
        {labels[status] || 'Pending'}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTimeDifference = (minutes) => {
    if (minutes === 0) return '0 min';
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    const sign = minutes > 0 ? '+' : '-';
    if (hours > 0 && mins > 0) return `${sign}${hours}h ${mins}m`;
    if (hours > 0) return `${sign}${hours}h`;
    return `${sign}${mins}m`;
  };

  const handleDiscrepancyClick = (timesheet) => {
    if (timesheet.time_discrepancy) {
      setDiscrepancyData({
        ...timesheet.time_discrepancy,
        user: timesheet.user,
        date: timesheet.date,
        project: timesheet.projekt?.name || timesheet.project?.name
      });
      setShowDiscrepancyModal(true);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-amber-500';
      case 'medium': return 'text-brand-500';
      case 'high': return 'text-red-500';
      default: return 'text-steel-400';
    }
  };

  const parseHours = (hoursStr) => {
    if (!hoursStr) return 0;
    if (typeof hoursStr === 'number') return hoursStr;
    const str = String(hoursStr);
    const match = str.match(/(\d+)h\s*(\d+)?min?/);
    if (match) {
      const h = parseInt(match[1]) || 0;
      const m = parseInt(match[2]) || 0;
      return h + m / 60;
    }
    return parseFloat(str) || 0;
  };

  const formatHoursDecimal = (hoursStr) => {
    const hours = parseHours(hoursStr);
    return `${hours.toFixed(2).replace('.', ',')}h`;
  };

  const getFullType = (timesheet) => {
    const workType = workTypes.find(wt => wt.code === timesheet.type);
    const typeName = workType ? workType.name : timesheet.type;
    if (timesheet.admin_type && timesheet.admin_type.name) {
      return `${typeName} - ${timesheet.admin_type.name}`;
    }
    return typeName;
  };

  const groupTimesheets = (timesheets) => {
    const grouped = [];
    const groups = {};

    timesheets.forEach(ts => {
      const groupKey = `${ts.user.id}-${ts.date}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          user: ts.user,
          date: ts.date,
          items: [],
          totalHours: 0,
          isGroup: true
        };
      }
      groups[groupKey].items.push(ts);
      const [hours, minutes] = ts.worked_hours.split('h ');
      const totalMinutes = parseInt(hours) * 60 + parseInt(minutes || 0);
      groups[groupKey].totalHours += totalMinutes;
    });

    Object.values(groups).forEach(group => {
      if (group.items.length === 1) {
        grouped.push({ ...group.items[0], isGroup: false });
      } else {
        const hours = Math.floor(group.totalHours / 60);
        const minutes = group.totalHours % 60;
        group.displayHours = `${hours}h ${minutes}min`;

        const statuses = group.items.map(item => item.status);
        const approvedCount = statuses.filter(s => s === 'approved').length;
        const rejectedCount = statuses.filter(s => s === 'rejected').length;
        const pendingCount = statuses.filter(s => s === 'pending').length;

        if (approvedCount === group.items.length) group.groupStatus = 'approved';
        else if (rejectedCount === group.items.length) group.groupStatus = 'rejected';
        else if (pendingCount === group.items.length) group.groupStatus = 'pending';
        else if (approvedCount > 0) group.groupStatus = 'partially-approved';
        else group.groupStatus = 'mixed';

        grouped.push(group);
      }
    });

    return grouped.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    });
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleResetFilters = () => {
    setFilters({ status: [], users: [], projects: [], categories: [], types: [], date_from: '', date_to: '' });
    setUserSearch('');
    setProjectSearch('');
    setCategorySearch('');
    setTypeSearch('');
  };

  const handleStatusToggle = (statusValue) => {
    setFilters(prev => ({
      ...prev,
      status: prev.status.includes(statusValue)
        ? prev.status.filter(s => s !== statusValue)
        : [...prev.status, statusValue]
    }));
  };

  const handleUserToggle = (userId) => {
    const newUsers = filters.users.includes(userId) ? filters.users.filter(id => id !== userId) : [...filters.users, userId];
    setFilters({ ...filters, users: newUsers });
  };

  const filteredUsers = availableUsers.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return fullName.includes(userSearch.toLowerCase());
  });

  const handleProjectToggle = (projectId) => {
    const newProjects = filters.projects.includes(projectId) ? filters.projects.filter(id => id !== projectId) : [...filters.projects, projectId];
    setFilters({ ...filters, projects: newProjects });
  };

  const filteredProjects = availableProjects.filter(project => project.name.toLowerCase().includes(projectSearch.toLowerCase()));

  const availableTypes = workTypes.map(type => ({
    value: type.code,
    label: type.name,
    category: type.category_name,
    color: type.category_color
  }));

  const handleTypeToggle = (typeValue) => {
    const newTypes = filters.types.includes(typeValue) ? filters.types.filter(t => t !== typeValue) : [...filters.types, typeValue];
    setFilters({ ...filters, types: newTypes });
  };

  const filteredTypes = availableTypes.filter(type => type.label.toLowerCase().includes(typeSearch.toLowerCase()));

  const handleCategoryToggle = (categoryId) => {
    const newCategories = filters.categories.includes(categoryId) ? filters.categories.filter(c => c !== categoryId) : [...filters.categories, categoryId];
    setFilters({ ...filters, categories: newCategories });
  };

  const filteredCategories = workCategories.filter(category => category.name.toLowerCase().includes(categorySearch.toLowerCase()));

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();

      if (selectedIds.length > 0) {
        selectedIds.forEach(id => params.append('timesheet_ids', id));
      } else {
        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => { if (v) params.append(key, v); });
          } else if (value && value !== 'all') {
            params.append(key, value);
          }
        });
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/timesheets/export-excel/?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'timesheet_report.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToastMessage('Export downloaded successfully');
    } catch (error) {
      showToastMessage('Error exporting to Excel');
    }
  };

  const hasFilters = filters.status.length > 0 || filters.users.length > 0 || filters.projects.length > 0 || filters.categories.length > 0 || filters.types.length > 0 || filters.date_from || filters.date_to;

  // Calculate work type summary (independent of projects)
  const workTypeSummary = {};
  const adminTypeSummary = {};
  timesheets.forEach(ts => {
    // By work type (e.g., WORK, TRAVEL, etc.)
    const typeCode = ts.type || 'OTHER';
    const workType = workTypes.find(wt => wt.code === typeCode);
    const typeName = workType?.name || typeCode;
    if (!workTypeSummary[typeCode]) {
      workTypeSummary[typeCode] = {
        code: typeCode,
        name: typeName,
        hours: 0,
        count: 0,
        color: workType?.category_color || '#6366f1'
      };
    }
    workTypeSummary[typeCode].hours += parseHours(ts.worked_hours);
    workTypeSummary[typeCode].count += 1;

    // By admin type (e.g., Travel, Work Day, Meeting, etc.)
    if (ts.admin_type?.name) {
      const adminName = ts.admin_type.name;
      if (!adminTypeSummary[adminName]) {
        adminTypeSummary[adminName] = { name: adminName, hours: 0, count: 0 };
      }
      adminTypeSummary[adminName].hours += parseHours(ts.worked_hours);
      adminTypeSummary[adminName].count += 1;
    }
  });
  const workTypeCards = Object.values(workTypeSummary).filter(t => t.hours > 0).sort((a, b) => b.hours - a.hours);
  const adminTypeCards = Object.values(adminTypeSummary).filter(t => t.hours > 0).sort((a, b) => b.hours - a.hours);

  // Calculate hours breakdown by status and type
  const approvedByType = {};
  const pendingByType = {};
  timesheets.forEach(ts => {
    const typeName = getFullType(ts);
    const hours = parseHours(ts.worked_hours);
    if (ts.status === 'approved') {
      if (!approvedByType[typeName]) approvedByType[typeName] = 0;
      approvedByType[typeName] += hours;
    } else if (ts.status === 'pending') {
      if (!pendingByType[typeName]) pendingByType[typeName] = 0;
      pendingByType[typeName] += hours;
    }
  });
  const approvedBreakdown = Object.entries(approvedByType).sort((a, b) => b[1] - a[1]);
  const pendingBreakdown = Object.entries(pendingByType).sort((a, b) => b[1] - a[1]);

  // Calculate pending hours
  const pendingHours = timesheets
    .filter(ts => ts.status === 'pending')
    .reduce((sum, ts) => sum + parseHours(ts.worked_hours), 0);

  // Calculate project summary
  const projectSummary = {};
  timesheets.forEach(ts => {
    const projectName = ts.projekt?.name || 'No Project';
    const projectId = ts.projekt?.id || 0;
    const projectColor = ts.projekt?.color || '#6B7280';
    if (!projectSummary[projectId]) {
      projectSummary[projectId] = { name: projectName, color: projectColor, totalHours: 0, types: {}, activityTypes: {} };
    }
    const hours = parseHours(ts.worked_hours);
    projectSummary[projectId].totalHours += hours;

    // Work types breakdown
    const typeName = getFullType(ts);
    if (!projectSummary[projectId].types[typeName]) {
      projectSummary[projectId].types[typeName] = 0;
    }
    projectSummary[projectId].types[typeName] += hours;

    // Activity types breakdown (work type with optional admin_type context)
    const workType = workTypes.find(wt => wt.code === ts.type);
    const workTypeName = workType?.name || ts.type || 'Other';
    const activityKey = ts.admin_type?.name ? `${workTypeName} - ${ts.admin_type.name}` : workTypeName;
    if (!projectSummary[projectId].activityTypes[activityKey]) {
      projectSummary[projectId].activityTypes[activityKey] = 0;
    }
    projectSummary[projectId].activityTypes[activityKey] += hours;
  });
  const projects = Object.values(projectSummary).filter(p => p.totalHours > 0);

  if (loading && timesheets.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-steel-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-steel-600 text-sm font-medium">Loading timesheets...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-black text-graphite-900">Timesheets Management</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('timesheets')}
            className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'timesheets' ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'bg-white border border-steel-200 text-steel-600 hover:bg-steel-50'}`}
          >
            Timesheets
          </button>
          {user?.isCompanyAdmin && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'settings' ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'bg-white border border-steel-200 text-steel-600 hover:bg-steel-50'}`}
            >
              Settings
            </button>
          )}
          <button
            onClick={() => navigate('/timesheets')}
            className="h-9 px-4 bg-graphite-900 text-white rounded-xl hover:bg-graphite-800 flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px] transition-all"
          >
            <QrCode size={14} /> QR Registration
          </button>
          {activeTab === 'timesheets' && user?.isCompanyAdmin && (
            <button onClick={handleExportExcel} className="h-9 px-4 bg-graphite-900 text-white rounded-xl hover:bg-graphite-800 flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px] transition-all">
              <FileSpreadsheet size={14} /> Export Excel
            </button>
          )}
        </div>
      </div>

      {activeTab === 'timesheets' && (
        <>
          {/* Top Section: Three Column Layout */}
          <div className="grid grid-cols-12 gap-4" style={{ marginBottom: '24px' }}>
            {/* Left Column - Summary */}
            <div className="col-span-4">
              {/* Summary Card */}
              <div className="bg-white rounded-xl border border-steel-200 shadow-sm">
                <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-3 border-b border-brand-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-4 w-4 text-brand-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-graphite-900">Summary</h3>
                      <p className="text-sm font-semibold text-brand-600">{formatHoursDecimal(stats.total_hours || '0h')} total</p>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Approved Card - Clickable */}
                    <div
                      className={`bg-emerald-50 rounded-lg border cursor-pointer hover:bg-emerald-100 transition-colors ${selectedSummaryCard === 'approved' ? 'border-2 border-emerald-500 shadow-sm' : 'border-emerald-100'}`}
                      onClick={() => { setSelectedSummaryCard(selectedSummaryCard === 'approved' ? null : 'approved'); setSelectedProject(null); }}
                    >
                      <div className="flex items-center justify-between px-3 py-2">
                        <div>
                          <p className="text-lg font-black text-emerald-600">{formatHoursDecimal(stats.approved_hours || '0h')}</p>
                          <p className="text-[10px] font-bold text-emerald-700 uppercase">Approved</p>
                        </div>
                        <ChevronRight size={14} className={`text-emerald-600 transition-transform ${selectedSummaryCard === 'approved' ? 'rotate-90' : ''}`} />
                      </div>
                    </div>

                    {/* Pending Card - Clickable */}
                    <div
                      className={`bg-amber-50 rounded-lg border cursor-pointer hover:bg-amber-100 transition-colors ${selectedSummaryCard === 'pending' ? 'border-2 border-amber-500 shadow-sm' : 'border-amber-100'}`}
                      onClick={() => { setSelectedSummaryCard(selectedSummaryCard === 'pending' ? null : 'pending'); setSelectedProject(null); }}
                    >
                      <div className="flex items-center justify-between px-3 py-2">
                        <div>
                          <p className="text-lg font-black text-amber-600">{formatHoursDecimal(pendingHours)}</p>
                          <p className="text-[10px] font-bold text-amber-700 uppercase">Pending ({stats.pending || 0})</p>
                        </div>
                        <ChevronRight size={14} className={`text-amber-600 transition-transform ${selectedSummaryCard === 'pending' ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Middle Column - Projects */}
            <div className="col-span-3">
              <div className="bg-white rounded-xl border border-steel-200 shadow-sm">
                <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-3 border-b border-brand-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="h-4 w-4 text-brand-600" />
                    </div>
                    <h3 className="text-sm font-bold text-graphite-900">Projects <span className="text-brand-600">({projects.length})</span></h3>
                  </div>
                </div>
                <div className="overflow-y-auto p-2 space-y-1.5" style={{ height: '240px' }}>
                  {projects.length === 0 ? (
                    <div className="text-center py-4">
                      <Briefcase className="h-6 w-6 text-steel-300 mx-auto mb-1" />
                      <p className="text-[10px] text-steel-500">No projects</p>
                    </div>
                  ) : (
                    projects.map((project, idx) => {
                      const projectColor = project.color || '#6B7280';
                      const isSelected = selectedProject === idx;
                      return (
                        <div
                          key={idx}
                          onClick={() => { setSelectedProject(isSelected ? null : idx); setSelectedSummaryCard(null); }}
                          className={`rounded-lg px-2 py-1.5 border cursor-pointer transition-all hover:shadow-sm ${isSelected ? 'border-2 shadow-sm' : 'border'}`}
                          style={{
                            borderColor: isSelected ? projectColor : '#e5e7eb',
                            backgroundColor: isSelected ? `${projectColor}08` : 'white'
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${projectColor}20` }}>
                                <Briefcase size={12} style={{ color: projectColor }} />
                              </div>
                              <p className="text-[11px] font-bold text-graphite-900 truncate">{project.name}</p>
                            </div>
                            <p className="text-xs font-black" style={{ color: projectColor }}>{formatHoursDecimal(project.totalHours)}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Details (Project or Status) */}
            <div className="col-span-5">
              {selectedSummaryCard ? (
                <div className={`bg-white rounded-xl border shadow-sm ${selectedSummaryCard === 'approved' ? 'border-emerald-200' : 'border-amber-200'}`}>
                  {/* Status Details Header */}
                  <div className={`bg-gradient-to-r ${selectedSummaryCard === 'approved' ? 'from-emerald-50' : 'from-amber-50'} to-white px-4 py-2 border-b ${selectedSummaryCard === 'approved' ? 'border-emerald-200' : 'border-amber-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedSummaryCard === 'approved' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                          <Clock size={16} className={selectedSummaryCard === 'approved' ? 'text-emerald-600' : 'text-amber-600'} />
                        </div>
                        <div>
                          <h2 className="text-sm font-bold text-graphite-900">{selectedSummaryCard === 'approved' ? 'Approved Hours' : 'Pending Hours'}</h2>
                          <p className="text-[10px] text-graphite-500">Breakdown by Type</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className={`text-lg font-black ${selectedSummaryCard === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {formatHoursDecimal(selectedSummaryCard === 'approved' ? stats.approved_hours : pendingHours)}
                          </p>
                          <p className="text-[9px] font-bold text-graphite-500 uppercase">Total</p>
                        </div>
                        <button
                          onClick={() => setSelectedSummaryCard(null)}
                          className="p-1.5 rounded-lg hover:bg-steel-100 text-graphite-400 hover:text-graphite-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Status Hours Breakdown */}
                  <div className="overflow-y-auto p-3" style={{ height: '240px' }}>
                    <p className="text-[9px] font-bold text-graphite-500 uppercase tracking-wide mb-2">Hours by Type</p>
                    <div className="space-y-1.5">
                      {(selectedSummaryCard === 'approved' ? approvedBreakdown : pendingBreakdown).map(([type, hours], idx) => {
                        const totalHrs = selectedSummaryCard === 'approved'
                          ? approvedBreakdown.reduce((sum, [, h]) => sum + h, 0)
                          : pendingBreakdown.reduce((sum, [, h]) => sum + h, 0);
                        const percentage = totalHrs > 0 ? (hours / totalHrs) * 100 : 0;
                        const colorClass = selectedSummaryCard === 'approved'
                          ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                          : 'bg-amber-50 border-amber-100 text-amber-700';
                        const barColor = selectedSummaryCard === 'approved' ? '#10b981' : '#f59e0b';
                        return (
                          <div key={type} className={`rounded-lg p-2 border ${colorClass}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[11px] font-semibold">{type}</span>
                              <span className="text-xs font-black">{formatHoursDecimal(hours)}</span>
                            </div>
                            <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${percentage}%`, backgroundColor: barColor }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {(selectedSummaryCard === 'approved' ? approvedBreakdown : pendingBreakdown).length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-[10px] text-steel-500">No data available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : selectedProject !== null && projects[selectedProject] ? (
                <div className="bg-white rounded-xl border border-steel-200 shadow-sm">
                  {/* Project Details Header */}
                  <div className="bg-gradient-to-r from-steel-50 to-white px-4 py-2 border-b border-steel-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${projects[selectedProject].color || '#6B7280'}20` }}>
                          <Briefcase size={16} style={{ color: projects[selectedProject].color || '#6B7280' }} />
                        </div>
                        <div>
                          <h2 className="text-sm font-bold text-graphite-900">{projects[selectedProject].name}</h2>
                          <p className="text-[10px] text-graphite-500">Project Details</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-lg font-black" style={{ color: projects[selectedProject].color || '#6B7280' }}>
                            {formatHoursDecimal(projects[selectedProject].totalHours)}
                          </p>
                          <p className="text-[9px] font-bold text-graphite-500 uppercase">Total</p>
                        </div>
                        <button
                          onClick={() => setSelectedProject(null)}
                          className="p-1.5 rounded-lg hover:bg-steel-100 text-graphite-400 hover:text-graphite-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Project Hours Breakdown */}
                  <div className="overflow-y-auto p-3" style={{ height: '240px' }}>
                    <p className="text-[9px] font-bold text-graphite-500 uppercase tracking-wide mb-2">Hours Breakdown</p>
                    <div className="space-y-1.5">
                      {/* Combined list: Activity Types first, then Work Types without admin_type */}
                      {(() => {
                        const activityEntries = Object.entries(projects[selectedProject].activityTypes || {});
                        const projectColor = projects[selectedProject].color || '#6B7280';
                        const allEntries = activityEntries
                          .map(([name, hrs]) => ({ name, hours: hrs, isActivity: true }))
                          .sort((a, b) => b.hours - a.hours);

                        return allEntries.map((entry, idx) => {
                          const percentage = (entry.hours / projects[selectedProject].totalHours) * 100;
                          const colors = ['bg-blue-50 border-blue-100 text-blue-700', 'bg-violet-50 border-violet-100 text-violet-700', 'bg-cyan-50 border-cyan-100 text-cyan-700', 'bg-pink-50 border-pink-100 text-pink-700', 'bg-indigo-50 border-indigo-100 text-indigo-700'];
                          const colorClass = colors[idx % colors.length];
                          return (
                            <div key={entry.name} className={`rounded-lg p-2 border ${colorClass}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[11px] font-semibold">{entry.name}</span>
                                <span className="text-xs font-black">{formatHoursDecimal(entry.hours)}</span>
                              </div>
                              <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${percentage}%`, backgroundColor: projectColor }}
                                />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-steel-200 shadow-sm flex flex-col items-center justify-center" style={{ height: '295px' }}>
                  <div className="text-center">
                    <Clock className="h-10 w-10 text-steel-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-graphite-500">Select to view details</p>
                    <p className="text-[10px] text-steel-400">Click on Approved, Pending or a Project</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Section: Filters and Table */}
          <div className="flex flex-col gap-3 mt-4">
          {/* Filters - Single Line, Sticky */}
          <div className="bg-white rounded-2xl border border-brand-200 shadow-sm px-4 py-3 sticky top-0 z-30" style={{ marginTop: '10px' }}>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Header */}
              <div className="flex items-center gap-2 pr-3 border-r border-steel-200">
                <Filter className="h-4 w-4 text-brand-500" />
                <span className="text-xs font-black text-graphite-900 uppercase">Filters</span>
                <span className="px-2 py-0.5 text-[9px] font-bold bg-brand-50 text-brand-600 rounded-md">
                  {totalCount}
                </span>
              </div>

              {/* Status Filter */}
              <div className="relative filter-dropdown-container flex items-center gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase whitespace-nowrap">
                  Status {filters.status.length > 0 && <span className="text-brand-500">({filters.status.length})</span>}
                </label>
                <div className="flex items-center">
                  <button
                    onClick={() => { setShowStatusDropdown(!showStatusDropdown); setShowCategoryDropdown(false); setShowTypeDropdown(false); setShowUserDropdown(false); setShowProjectDropdown(false); }}
                    className={`h-8 pl-2 pr-2 min-w-[100px] flex items-center justify-between gap-1 border rounded-lg text-xs font-medium transition-colors ${filters.status.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'} ${filters.status.length > 0 ? 'rounded-r-none border-r-0' : ''}`}
                  >
                    <span className="truncate max-w-[60px]">{filters.status.length === 0 ? 'All' : `${filters.status.length} selected`}</span>
                    <ChevronDown size={14} className="flex-shrink-0" />
                  </button>
                  {filters.status.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, status: [] }); }}
                      className="h-8 px-1.5 bg-red-50 border border-red-200 rounded-r-lg text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Clear status filter"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {showStatusDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="py-1">
                      {[
                        { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
                        { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
                        { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' }
                      ].map(status => (
                        <label key={status.value} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.status.includes(status.value) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.status.includes(status.value)} onChange={() => handleStatusToggle(status.value)} className="w-4 h-4 text-brand-500 rounded" />
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Category Filter */}
              <div className="relative filter-dropdown-container flex items-center gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase whitespace-nowrap">
                  Category {filters.categories.length > 0 && <span className="text-brand-500">({filters.categories.length})</span>}
                </label>
                <div className="flex items-center">
                  <button
                    onClick={() => { setShowCategoryDropdown(!showCategoryDropdown); setShowStatusDropdown(false); setShowTypeDropdown(false); setShowUserDropdown(false); setShowProjectDropdown(false); }}
                    className={`h-8 pl-2 pr-2 min-w-[100px] flex items-center justify-between gap-1 border rounded-lg text-xs font-medium transition-colors ${filters.categories.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'} ${filters.categories.length > 0 ? 'rounded-r-none border-r-0' : ''}`}
                  >
                    <span className="truncate max-w-[60px]">{filters.categories.length === 0 ? 'All' : `${filters.categories.length} selected`}</span>
                    <ChevronDown size={14} className="flex-shrink-0" />
                  </button>
                  {filters.categories.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, categories: [] }); }}
                      className="h-8 px-1.5 bg-red-50 border border-red-200 rounded-r-lg text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Clear category filter"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {showCategoryDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredCategories.map(category => (
                        <label key={category.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.categories.includes(category.id.toString()) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.categories.includes(category.id.toString())} onChange={() => handleCategoryToggle(category.id.toString())} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="w-3 h-3 rounded" style={{ backgroundColor: category.color }} />
                          <span className="text-sm font-medium text-graphite-700 truncate">{category.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Type Filter */}
              <div className="relative filter-dropdown-container flex items-center gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase whitespace-nowrap">
                  Type {filters.types.length > 0 && <span className="text-brand-500">({filters.types.length})</span>}
                </label>
                <div className="flex items-center">
                  <button
                    onClick={() => { setShowTypeDropdown(!showTypeDropdown); setShowStatusDropdown(false); setShowCategoryDropdown(false); setShowUserDropdown(false); setShowProjectDropdown(false); }}
                    className={`h-8 pl-2 pr-2 min-w-[100px] flex items-center justify-between gap-1 border rounded-lg text-xs font-medium transition-colors ${filters.types.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'} ${filters.types.length > 0 ? 'rounded-r-none border-r-0' : ''}`}
                  >
                    <span className="truncate max-w-[60px]">{filters.types.length === 0 ? 'All' : `${filters.types.length} selected`}</span>
                    <ChevronDown size={14} className="flex-shrink-0" />
                  </button>
                  {filters.types.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, types: [] }); }}
                      className="h-8 px-1.5 bg-red-50 border border-red-200 rounded-r-lg text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Clear type filter"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {showTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={typeSearch} onChange={(e) => setTypeSearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredTypes.map(type => (
                        <label key={type.value} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.types.includes(type.value) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.types.includes(type.value)} onChange={() => handleTypeToggle(type.value)} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">{type.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* User Filter */}
              <div className="relative filter-dropdown-container flex items-center gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase whitespace-nowrap">
                  User {filters.users.length > 0 && <span className="text-brand-500">({filters.users.length})</span>}
                </label>
                <div className="flex items-center">
                  <button
                    onClick={() => { setShowUserDropdown(!showUserDropdown); setShowStatusDropdown(false); setShowCategoryDropdown(false); setShowTypeDropdown(false); setShowProjectDropdown(false); }}
                    className={`h-8 pl-2 pr-2 min-w-[100px] flex items-center justify-between gap-1 border rounded-lg text-xs font-medium transition-colors ${filters.users.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'} ${filters.users.length > 0 ? 'rounded-r-none border-r-0' : ''}`}
                  >
                    <span className="truncate max-w-[60px]">{filters.users.length === 0 ? 'All' : `${filters.users.length} selected`}</span>
                    <ChevronDown size={14} className="flex-shrink-0" />
                  </button>
                  {filters.users.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, users: [] }); }}
                      className="h-8 px-1.5 bg-red-50 border border-red-200 rounded-r-lg text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Clear user filter"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {showUserDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredUsers.map(user => (
                        <label key={user.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.users.includes(user.id.toString()) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.users.includes(user.id.toString())} onChange={() => handleUserToggle(user.id.toString())} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">{user.first_name} {user.last_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Project Filter */}
              <div className="relative filter-dropdown-container flex items-center gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase whitespace-nowrap">
                  Project {filters.projects.length > 0 && <span className="text-brand-500">({filters.projects.length})</span>}
                </label>
                <div className="flex items-center">
                  <button
                    onClick={() => { setShowProjectDropdown(!showProjectDropdown); setShowStatusDropdown(false); setShowCategoryDropdown(false); setShowTypeDropdown(false); setShowUserDropdown(false); }}
                    className={`h-8 pl-2 pr-2 min-w-[100px] flex items-center justify-between gap-1 border rounded-lg text-xs font-medium transition-colors ${filters.projects.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-graphite-700'} ${filters.projects.length > 0 ? 'rounded-r-none border-r-0' : ''}`}
                  >
                    <span className="truncate max-w-[60px]">{filters.projects.length === 0 ? 'All' : `${filters.projects.length} selected`}</span>
                    <ChevronDown size={14} className="flex-shrink-0" />
                  </button>
                  {filters.projects.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, projects: [] }); }}
                      className="h-8 px-1.5 bg-red-50 border border-red-200 rounded-r-lg text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Clear project filter"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {showProjectDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-steel-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-steel-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-steel-400" />
                        <input type="text" placeholder="Search..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} onClick={(e) => e.stopPropagation()} className="w-full pl-8 pr-3 py-1.5 border border-steel-200 rounded-lg text-sm focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredProjects.map(project => (
                        <label key={project.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-steel-50 ${filters.projects.includes(project.id.toString()) ? 'bg-brand-50' : ''}`}>
                          <input type="checkbox" checked={filters.projects.includes(project.id.toString())} onChange={() => handleProjectToggle(project.id.toString())} className="w-4 h-4 text-brand-500 rounded" />
                          <span className="text-sm font-medium text-graphite-700 truncate">{project.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Date From */}
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase">From</label>
                <div className="flex items-center">
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    className={`h-8 px-2 border rounded-lg text-xs font-medium focus:ring-1 focus:ring-brand-500 ${filters.date_from ? 'bg-brand-50 border-brand-200 text-brand-700 rounded-r-none border-r-0' : 'bg-white border-steel-200 text-graphite-700'}`}
                  />
                  {filters.date_from && (
                    <button
                      onClick={() => setFilters({ ...filters, date_from: '' })}
                      className="h-8 px-1.5 bg-red-50 border border-red-200 rounded-r-lg text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Clear from date"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              {/* Date To */}
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase">To</label>
                <div className="flex items-center">
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    className={`h-8 px-2 border rounded-lg text-xs font-medium focus:ring-1 focus:ring-brand-500 ${filters.date_to ? 'bg-brand-50 border-brand-200 text-brand-700 rounded-r-none border-r-0' : 'bg-white border-steel-200 text-graphite-700'}`}
                  />
                  {filters.date_to && (
                    <button
                      onClick={() => setFilters({ ...filters, date_to: '' })}
                      className="h-8 px-1.5 bg-red-50 border border-red-200 rounded-r-lg text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Clear to date"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              {/* Clear Filters */}
              {hasFilters && (
                <button
                  onClick={handleResetFilters}
                  className="h-8 px-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-bold hover:bg-red-100 transition-colors flex items-center gap-1"
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          {timesheets.length === 0 ? (
            <div className="bg-white rounded-2xl border border-steel-100 shadow-sm p-10 text-center flex-1">
              <div className="w-12 h-12 bg-steel-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Clock className="h-6 w-6 text-steel-400" />
              </div>
              <p className="text-sm font-bold text-graphite-900 mb-1">No timesheets found</p>
              <p className="text-xs text-steel-400">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-brand-200 shadow-sm overflow-hidden">
              {/* Toolbar */}
              <div className="px-4 py-2 border-b border-steel-100 flex items-center justify-between bg-steel-50">
                <button
                  onClick={() => {
                    const grouped = groupTimesheets(timesheets);
                    const allGroupKeys = grouped.filter(item => item.isGroup).map(item => item.key);
                    const allExpanded = allGroupKeys.every(key => expandedGroups[key]);
                    if (allExpanded) setExpandedGroups({});
                    else {
                      const newExpanded = {};
                      allGroupKeys.forEach(key => { newExpanded[key] = true; });
                      setExpandedGroups(newExpanded);
                    }
                  }}
                  className="text-xs font-bold text-graphite-700 hover:text-brand-500 flex items-center gap-1"
                >
                  {(() => {
                    const grouped = groupTimesheets(timesheets);
                    const allGroupKeys = grouped.filter(item => item.isGroup).map(item => item.key);
                    const allExpanded = allGroupKeys.length > 0 && allGroupKeys.every(key => expandedGroups[key]);
                    return allExpanded ? <><ChevronDown size={14} /> Collapse All</> : <><ChevronRight size={14} /> Expand All</>;
                  })()}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-steel-50 border-b border-steel-100">
                      <th className="px-3 py-3 text-left w-10">
                        <input type="checkbox" checked={selectedIds.length === timesheets.length && timesheets.length > 0} onChange={handleSelectAll} className="w-4 h-4 text-brand-500 rounded" />
                      </th>
                      <th className="px-3 py-3 text-left w-10"></th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">User</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Project</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Check In/Out</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider cursor-pointer hover:text-graphite-900" onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}>
                        Date {sortDirection === 'desc' ? '▼' : '▲'}
                      </th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Time</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Hours</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Type</th>
                      <th className="px-3 py-3 text-center text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-3 text-left text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Notes</th>
                      <th className="px-3 py-3 text-center text-[10px] font-bold text-graphite-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-steel-100">
                    {groupTimesheets(timesheets).map((item) => {
                      if (item.isGroup) {
                        const isExpanded = expandedGroups[item.key];
                        const hasDiscrepancy = item.items.some(ts => ts.time_discrepancy?.has_discrepancy);
                        return (
                          <React.Fragment key={item.key}>
                            <tr className={`hover:bg-steel-50 cursor-pointer ${hasDiscrepancy ? 'bg-amber-50/50' : ''}`} onClick={() => toggleGroup(item.key)}>
                              <td className="px-3 py-2">
                                <span className="text-steel-400">{isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                              </td>
                              <td className="px-3 py-2">
                                {item.user.profile_picture ? (
                                  <img src={`${process.env.REACT_APP_API_URL}${item.user.profile_picture}`} alt="" className="w-8 h-8 rounded-lg object-cover border border-steel-200" />
                                ) : (
                                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-[10px] font-black text-white">
                                    {item.user.full_name?.charAt(0) || 'U'}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm font-bold text-graphite-900">{item.user.full_name}</td>
                              <td className="px-3 py-2 text-sm text-steel-600">
                                {(() => {
                                  const uniqueProjects = [...new Set(item.items.map(ts => ts.projekt?.name).filter(Boolean))];
                                  if (uniqueProjects.length === 1) return uniqueProjects[0];
                                  if (uniqueProjects.length > 1) return `Multiple (${uniqueProjects.length})`;
                                  return '-';
                                })()}
                              </td>
                              <td className="px-3 py-2">
                                {(() => {
                                  const checkIns = item.items.map(ts => ts.qr_check_in).filter(Boolean);
                                  const checkOuts = item.items.map(ts => ts.qr_check_out).filter(Boolean);
                                  return (
                                    <div className="space-y-0.5">
                                      <div className="flex items-center gap-1 text-xs text-emerald-600">
                                        <LogIn size={12} />
                                        <span>{checkIns.length > 0 ? checkIns[0] : '-'}</span>
                                        {hasDiscrepancy && <AlertTriangle size={12} className="text-amber-500" />}
                                      </div>
                                      <div className="flex items-center gap-1 text-xs text-red-500">
                                        <LogOut size={12} />
                                        <span>{checkOuts.length > 0 ? checkOuts[checkOuts.length - 1] : '-'}</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-3 py-2 text-sm text-steel-600">{formatDate(item.date)}</td>
                              <td className="px-3 py-2">
                                <span className="px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-steel-600 rounded-md">{item.items.length} entries</span>
                              </td>
                              <td className="px-3 py-2 text-sm font-bold text-graphite-900">{item.displayHours}</td>
                              <td className="px-3 py-2">
                                {(() => {
                                  const uniqueTypes = [...new Set(item.items.map(ts => ts.type))];
                                  if (uniqueTypes.length === 1) {
                                    const workType = workTypes.find(wt => wt.code === uniqueTypes[0]);
                                    return <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md">{workType?.name || uniqueTypes[0]}</span>;
                                  }
                                  return <span className="px-2 py-0.5 text-[9px] font-bold bg-steel-100 text-steel-600 rounded-md">Multiple</span>;
                                })()}
                              </td>
                              <td className="px-3 py-2 text-center">{getStatusBadge(item.groupStatus)}</td>
                              <td className="px-3 py-2 text-xs text-steel-400">{item.items.length} entries</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={(e) => { e.stopPropagation(); item.items.forEach(ts => handleSingleApprove(ts.id)); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 hover:bg-emerald-100 transition-colors" title="Approve All">
                                    <CheckCircle size={14} />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setSelectedIds(item.items.map(ts => ts.id)); setShowRejectModal(true); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors" title="Reject All">
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && item.items.map((ts) => (
                              <tr key={ts.id} className="bg-steel-50/50 hover:bg-steel-100">
                                <td className="px-3 py-2 pl-6">
                                  <input type="checkbox" checked={selectedIds.includes(ts.id)} onChange={() => handleSelectOne(ts.id)} className="w-4 h-4 text-brand-500 rounded" />
                                </td>
                                <td className="px-3 py-2"></td>
                                <td className="px-3 py-2 text-sm text-steel-600 pl-6">↳ {ts.user.full_name}</td>
                                <td className="px-3 py-2 text-sm text-graphite-700">{ts.projekt?.name || 'N/A'}</td>
                                <td className="px-3 py-2">
                                  <div className="space-y-0.5">
                                    <div className="flex items-center gap-1 text-xs text-emerald-600">
                                      <LogIn size={12} />
                                      <span>{ts.qr_check_in || '-'}</span>
                                      {ts.time_discrepancy?.has_discrepancy && (
                                        <AlertTriangle size={12} className={getSeverityColor(ts.time_discrepancy.severity)} onClick={(e) => { e.stopPropagation(); handleDiscrepancyClick(ts); }} style={{ cursor: 'pointer' }} />
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-red-500">
                                      <LogOut size={12} />
                                      <span>{ts.qr_check_out || '-'}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm text-graphite-700">{formatDate(ts.date)}</td>
                                <td className="px-3 py-2 text-sm text-graphite-700">{ts.start_time} - {ts.end_time}</td>
                                <td className="px-3 py-2 text-sm font-bold text-graphite-900">{ts.worked_hours}</td>
                                <td className="px-3 py-2">
                                  <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md">{getFullType(ts)}</span>
                                </td>
                                <td className="px-3 py-2 text-center">{getStatusBadge(ts.status)}</td>
                                <td className="px-3 py-2 text-xs text-graphite-700 max-w-[150px] truncate" title={ts.note}>{ts.note || '-'}</td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center justify-center gap-1">
                                    {ts.status === 'pending' && (
                                      <>
                                        <button onClick={(e) => { e.stopPropagation(); handleSingleApprove(ts.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 hover:bg-emerald-100 transition-colors" title="Approve">
                                          <CheckCircle size={14} />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); handleSingleRejectClick(ts.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors" title="Reject">
                                          <XCircle size={14} />
                                        </button>
                                      </>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); handleEditClick(ts); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors" title="Edit">
                                      <Edit2 size={14} />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleHistoryClick(ts); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-50 text-violet-500 hover:bg-violet-100 transition-colors" title="History">
                                      <History size={14} />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleSingleDelete(ts.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-steel-50 text-steel-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete">
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      } else {
                        const ts = item;
                        return (
                          <tr key={ts.id} className={`hover:bg-steel-50 ${ts.time_discrepancy?.has_discrepancy ? 'bg-amber-50/50' : ''}`}>
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={selectedIds.includes(ts.id)} onChange={() => handleSelectOne(ts.id)} className="w-4 h-4 text-brand-500 rounded" />
                            </td>
                            <td className="px-3 py-2">
                              {ts.user.profile_picture ? (
                                <img src={`${process.env.REACT_APP_API_URL}${ts.user.profile_picture}`} alt="" className="w-8 h-8 rounded-lg object-cover border border-steel-200" />
                              ) : (
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-[10px] font-black text-white">
                                  {ts.user.full_name?.charAt(0) || 'U'}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm font-bold text-graphite-900">{ts.user.full_name}</td>
                            <td className="px-3 py-2 text-sm text-graphite-700">{ts.projekt?.name || 'N/A'}</td>
                            <td className="px-3 py-2">
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-1 text-xs text-emerald-600">
                                  <LogIn size={12} />
                                  <span>{ts.qr_check_in || '-'}</span>
                                  {ts.time_discrepancy?.has_discrepancy && (
                                    <AlertTriangle size={12} className={getSeverityColor(ts.time_discrepancy.severity)} onClick={(e) => { e.stopPropagation(); handleDiscrepancyClick(ts); }} style={{ cursor: 'pointer' }} />
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-red-500">
                                  <LogOut size={12} />
                                  <span>{ts.qr_check_out || '-'}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm text-graphite-700">{formatDate(ts.date)}</td>
                            <td className="px-3 py-2 text-sm text-graphite-700">{ts.start_time} - {ts.end_time}</td>
                            <td className="px-3 py-2 text-sm font-bold text-graphite-900">{ts.worked_hours}</td>
                            <td className="px-3 py-2">
                              <span className="px-2 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-600 rounded-md">{getFullType(ts)}</span>
                            </td>
                            <td className="px-3 py-2 text-center">{getStatusBadge(ts.status)}</td>
                            <td className="px-3 py-2 text-xs text-graphite-700 max-w-[150px] truncate" title={ts.note}>{ts.note || '-'}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-center gap-1">
                                {ts.status === 'pending' && (
                                  <>
                                    <button onClick={() => handleSingleApprove(ts.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 hover:bg-emerald-100 transition-colors" title="Approve">
                                      <CheckCircle size={14} />
                                    </button>
                                    <button onClick={() => handleSingleRejectClick(ts.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors" title="Reject">
                                      <XCircle size={14} />
                                    </button>
                                  </>
                                )}
                                <button onClick={() => handleEditClick(ts)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors" title="Edit">
                                  <Edit2 size={14} />
                                </button>
                                <button onClick={() => handleHistoryClick(ts)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-50 text-violet-500 hover:bg-violet-100 transition-colors" title="History">
                                  <History size={14} />
                                </button>
                                <button onClick={() => handleSingleDelete(ts.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-steel-50 text-steel-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-4 border-t border-steel-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-steel-600 font-medium">
                    Showing <span className="font-bold text-graphite-900">{totalCount > 0 ? ((currentPage - 1) * pageSize) + 1 : 0}-{Math.min(currentPage * pageSize, totalCount)}</span> of <span className="font-bold text-graphite-900">{totalCount}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-steel-600 font-medium">Rows:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                      className="h-9 pl-3 pr-8 border border-steel-200 rounded-lg text-sm font-medium text-graphite-700 bg-white focus:ring-1 focus:ring-brand-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronsLeft size={16} />
                    </button>
                    <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    <div className="flex items-center gap-1 mx-1">
                      {(() => {
                        const pages = [];
                        const showPages = 5;
                        let start = Math.max(1, currentPage - Math.floor(showPages / 2));
                        let end = Math.min(totalPages, start + showPages - 1);
                        if (end - start + 1 < showPages) start = Math.max(1, end - showPages + 1);
                        for (let i = start; i <= end; i++) {
                          pages.push(
                            <button key={i} onClick={() => setCurrentPage(i)} className={`h-8 w-8 flex items-center justify-center text-xs font-bold rounded-lg transition-colors ${currentPage === i ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-steel-600 bg-steel-100 hover:bg-steel-200'}`}>{i}</button>
                          );
                        }
                        return pages;
                      })()}
                    </div>
                    <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronRightIcon size={16} />
                    </button>
                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="h-8 w-8 flex items-center justify-center text-steel-600 bg-steel-100 rounded-lg hover:bg-steel-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && user?.isCompanyAdmin && (
        <TimesheetSettings />
      )}

      {/* Action Bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-graphite-900 text-white rounded-2xl shadow-2xl px-6 py-3 flex items-center gap-4">
          <span className="text-sm font-bold">{selectedIds.length} selected</span>
          <div className="h-6 w-px bg-graphite-700" />
          <button onClick={handleApprove} className="h-8 px-3 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-colors flex items-center gap-1">
            <CheckCircle size={14} /> Approve
          </button>
          <button onClick={handleReject} className="h-8 px-3 bg-red-500 text-white rounded-lg text-xs font-bold hover:bg-red-600 transition-colors flex items-center gap-1">
            <XCircle size={14} /> Reject
          </button>
          <button onClick={() => { const ts = timesheets.find(t => t.id === selectedIds[0]); if (ts) handleEditClick(ts); }} disabled={selectedIds.length !== 1} className="h-8 px-3 bg-graphite-700 text-white rounded-lg text-xs font-bold hover:bg-steel-600 disabled:opacity-40 transition-colors flex items-center gap-1">
            <Edit2 size={14} /> Edit
          </button>
          <button onClick={handleDelete} className="h-8 px-3 bg-graphite-700 text-white rounded-lg text-xs font-bold hover:bg-steel-600 transition-colors flex items-center gap-1">
            <Trash2 size={14} /> Delete
          </button>
          <button onClick={() => setSelectedIds([])} className="h-8 px-3 bg-graphite-700 text-white rounded-lg text-xs font-bold hover:bg-steel-600 transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* Modals */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowRejectModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-red-500" />
              </div>
              <h2 className="text-sm font-black text-graphite-900 uppercase">Reject Timesheets</h2>
            </div>
            <div className="p-4">
              <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Rejection Reason</label>
              <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Enter reason..." rows={4} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 placeholder-steel-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none" />
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowRejectModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={confirmReject} className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 font-bold text-xs uppercase tracking-wide transition-all">Reject</button>
            </div>
          </div>
        </div>
      )}

      {showSingleRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSingleRejectModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-red-500" />
              </div>
              <h2 className="text-sm font-black text-graphite-900 uppercase">Reject Timesheet</h2>
            </div>
            <div className="p-4">
              <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Rejection Reason</label>
              <textarea value={singleRejectionReason} onChange={(e) => setSingleRejectionReason(e.target.value)} placeholder="Enter reason..." rows={4} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 placeholder-steel-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none" />
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowSingleRejectModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={confirmSingleReject} className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 font-bold text-xs uppercase tracking-wide transition-all">Reject</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <Trash2 className="h-4 w-4 text-red-500" />
              </div>
              <h2 className="text-sm font-black text-graphite-900 uppercase">Confirm Delete</h2>
            </div>
            <div className="p-4">
              <p className="text-sm text-graphite-700">
                {deleteType === 'single' ? 'Delete this timesheet? This cannot be undone.' : `Delete ${selectedIds.length} timesheet(s)? This cannot be undone.`}
              </p>
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowDeleteModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={confirmDelete} className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 font-bold text-xs uppercase tracking-wide transition-all">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && historyTimesheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowHistoryModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <History className="h-4 w-4 text-violet-600" />
                </div>
                <h2 className="text-sm font-black text-graphite-900 uppercase">Timesheet History</h2>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-400"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="p-3 bg-steel-50 rounded-xl space-y-1">
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">User:</strong> {historyTimesheet.user.full_name}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Project:</strong> {historyTimesheet.projekt?.name || 'N/A'}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Date:</strong> {formatDate(historyTimesheet.date)}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Time:</strong> {historyTimesheet.start_time} - {historyTimesheet.end_time}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Hours:</strong> {historyTimesheet.worked_hours}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Type:</strong> {getFullType(historyTimesheet)}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Status:</strong> {historyTimesheet.status}</p>
              </div>
              {historyTimesheet.approved_by && (
                <div className="p-3 bg-emerald-50 rounded-xl space-y-1">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase">Approval Info</p>
                  <p className="text-xs text-steel-600">Approved by: {historyTimesheet.approved_by}</p>
                  <p className="text-xs text-steel-600">Date: {formatDate(historyTimesheet.approved_date)}</p>
                </div>
              )}
              {historyTimesheet.rejected_by && (
                <div className="p-3 bg-red-50 rounded-xl space-y-1">
                  <p className="text-[10px] font-bold text-red-600 uppercase">Rejection Info</p>
                  <p className="text-xs text-steel-600">Rejected by: {historyTimesheet.rejected_by}</p>
                  <p className="text-xs text-steel-600">Date: {formatDate(historyTimesheet.rejected_date)}</p>
                  {historyTimesheet.rejection_reason && <p className="text-xs text-steel-600">Reason: {historyTimesheet.rejection_reason}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingTimesheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEditModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                  <Edit2 className="h-4 w-4 text-brand-500" />
                </div>
                <div>
                  <h2 className="text-sm font-black text-graphite-900 uppercase">Edit Timesheet</h2>
                  <p className="text-[10px] text-steel-400">{editingTimesheet.user.full_name}</p>
                </div>
              </div>
              <button onClick={() => setShowEditModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-400"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Date</label>
                  <input type="date" value={editFormData.date} onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Project</label>
                  <select value={editFormData.projekt} onChange={(e) => setEditFormData({ ...editFormData, projekt: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                    <option value="">Select Project</option>
                    {availableProjects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Start Time</label>
                  <input type="time" value={editFormData.start_time} onChange={(e) => setEditFormData({ ...editFormData, start_time: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">End Time</label>
                  <input type="time" value={editFormData.end_time} onChange={(e) => setEditFormData({ ...editFormData, end_time: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Break (min)</label>
                  <input type="number" min="0" max="480" value={editFormData.break_duration} onChange={(e) => setEditFormData({ ...editFormData, break_duration: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Work Type</label>
                  <select value={editFormData.type} onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                    <option value="">Select Type</option>
                    {workTypes.map(type => <option key={type.id} value={type.code}>{type.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Admin Type</label>
                  <select value={editFormData.admin_type} onChange={(e) => setEditFormData({ ...editFormData, admin_type: e.target.value })} className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                    <option value="">None</option>
                    {adminTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-steel-400 uppercase tracking-widest mb-1.5">Note</label>
                  <input type="text" value={editFormData.note} onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })} placeholder="Optional note..." className="w-full px-3 py-2.5 border border-steel-200 rounded-xl text-sm font-medium text-graphite-900 placeholder-steel-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowEditModal(false)} className="h-9 px-4 text-steel-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors">Cancel</button>
              <button onClick={handleSaveEdit} className="h-9 px-4 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-brand-500/20">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {showDiscrepancyModal && discrepancyData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDiscrepancyModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                </div>
                <h2 className="text-sm font-black text-graphite-900 uppercase">Time Discrepancy</h2>
              </div>
              <button onClick={() => setShowDiscrepancyModal(false)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-400"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="p-3 bg-steel-50 rounded-xl space-y-1">
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">User:</strong> {discrepancyData.user?.full_name}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Project:</strong> {discrepancyData.project}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Date:</strong> {formatDate(discrepancyData.date)}</p>
                <p className="text-xs text-steel-500"><strong className="text-graphite-700">Severity:</strong> <span className={`font-bold uppercase ${getSeverityColor(discrepancyData.severity)}`}>{discrepancyData.severity}</span></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-emerald-50 rounded-xl">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase mb-2">QR System (Actual)</p>
                  <p className="text-xs text-steel-600">Check-In: {discrepancyData.qr_start}</p>
                  <p className="text-xs text-steel-600">Check-Out: {discrepancyData.qr_end}</p>
                </div>
                <div className="p-3 bg-brand-50 rounded-xl">
                  <p className="text-[10px] font-bold text-brand-600 uppercase mb-2">Timesheet (Submitted)</p>
                  <p className="text-xs text-steel-600">Start: {discrepancyData.ts_start}</p>
                  <p className="text-xs text-steel-600">End: {discrepancyData.ts_end}</p>
                </div>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl space-y-1">
                <p className="text-[10px] font-bold text-amber-600 uppercase">Analysis</p>
                <p className="text-xs text-steel-600">Start Diff: {formatTimeDifference(discrepancyData.start_diff_minutes)}</p>
                <p className="text-xs text-steel-600">End Diff: {formatTimeDifference(discrepancyData.end_diff_minutes)}</p>
                <p className="text-xs text-steel-600">Total Diff: {formatTimeDifference(discrepancyData.total_diff_minutes)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 bg-graphite-900 text-white text-sm font-bold rounded-xl shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default TimesheetManagement;
